import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { DownloadFilterCriteria, toFilter } from '../models/download-filter.model';
import { Observable, Subscription } from 'rxjs';
import { downloadBlob, generateDateFilename, handleDownloadError } from './download-helpers';
import { ToastrService } from '../services/toastr-service';

export type TaggedFilterValue = string | string[] | boolean | boolean[] | null | undefined | any[];
export type TaggedFilter = { key: string; value: TaggedFilterValue };
export type FilterTagDefinition = { key: string; control: string; downloadColumn?: string };

export function removeTaggedValue(taggedFilters: TaggedFilter[], key: string, value: string): string[] {
  const taggedFilter = taggedFilters.find((filter) => filter.key === key);
  const updatedValues = Array.isArray(taggedFilter?.value) ? taggedFilter.value.filter((item) => item !== value) : [];

  if (taggedFilter) {
    taggedFilter.value = updatedValues;
  }

  return updatedValues;
}

export function updateTaggedFilters(taggedFilters: TaggedFilter[], form: FormGroup, definitions: FilterTagDefinition[]): void {
  definitions.forEach(({ key, control }) => {
    const value = form.get(control)?.value ?? null;
    const existingValue = taggedFilters.find((filter) => filter.key === key)?.value;
    if (existingValue === value) {
      return;
    }

    const index = taggedFilters.findIndex((filter) => filter.key === key);
    if (index >= 0) {
      taggedFilters.splice(index, 1);
    }
    if (value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)) {
      taggedFilters.unshift({ key, value });
    }
  });
}

export function buildDownloadFilters(form: FormGroup, definitions: FilterTagDefinition[]): DownloadFilterCriteria[] {
  return definitions.flatMap(({ control, downloadColumn }) => {
    const values = form.get(control)?.value as string[] | null;
    return downloadColumn && values?.length ? [toFilter(downloadColumn, values)] : [];
  });
}

export function resetControlValue(control: AbstractControl | null, value: string[] | null): void {
  control?.setValue(value);
}

export function ensureTaggedFilterControls(
  formBuilder: FormBuilder,
  filterForm: FormGroup,
  searchForm: FormGroup,
  definitions: FilterTagDefinition[],
  applyControl: string,
  searchControl: string,
): void {
  const controls: { name: string; control: AbstractControl }[] = [
    { name: applyControl, control: formBuilder.control<boolean | null>(null) },
  ];
  definitions.forEach(({ control }) => controls.push({ name: control, control: formBuilder.control<string[] | null>(null) }));
  controls.forEach(({ name, control }) => {
    if (!filterForm.get(name)) filterForm.addControl(name, control);
  });
  if (!searchForm.get(searchControl)) searchForm.addControl(searchControl, formBuilder.control<boolean | null>(null));
}

export function restoreTaggedFilterForm(
  filterForm: FormGroup,
  savedValue: object | null,
  applyControl: string,
  updateTags: () => void,
): void {
  if (!savedValue) return;

  filterForm.patchValue(savedValue, { emitEvent: false });
  updateTags();
  setTimeout(() => filterForm.get(applyControl)?.setValue(true));
}

export function subscribeToTaggedFilterChanges(
  filterForm: FormGroup,
  searchForm: FormGroup,
  applyControl: string,
  searchControl: string,
  onApply: () => void,
  onSearch: () => void,
): Subscription[] {
  const applySubscription = filterForm.get(applyControl)!.valueChanges.subscribe((value) => {
    if (value) onApply();
  });
  const searchSubscription = searchForm.get(searchControl)!.valueChanges.subscribe(onSearch);
  return [applySubscription, searchSubscription];
}

export function restoreTaggedFilterSelections(
  filterForm: FormGroup,
  isOpen: boolean,
  savedValue: Record<string, unknown> | null,
  definitions: FilterTagDefinition[],
): boolean {
  if (!isOpen) return false;

  const values = definitions.reduce<Record<string, unknown>>((result, { control }) => {
    result[control] = savedValue?.[control] ?? null;
    return result;
  }, {});
  filterForm.patchValue(values, { emitEvent: false });
  return true;
}

export function clearTaggedFilterForm(
  formGroup: FormGroup,
  filterForm: FormGroup,
  applyControl: string,
  clearSavedValue: () => void,
  updateTags: () => void,
): void {
  formGroup.reset();
  filterForm.get(applyControl)?.setValue(true);
  clearSavedValue();
  updateTags();
}

export function downloadFilteredFile(
  request: Observable<Blob>,
  filenamePrefix: string,
  toastrService: ToastrService,
  onComplete: () => void,
): void {
  request.subscribe({
    next: (blob) => {
      downloadBlob(blob, generateDateFilename(filenamePrefix));
      onComplete();
    },
    error: (error) => {
      handleDownloadError(error, toastrService);
      onComplete();
    },
  });
}
